import { useEffect, useState } from "react";
import type { InstanceActivityEvent } from "@keydials/api-client";
import { useWorkspaceServices } from "../../context";
import { activityActor } from "../ActivityPanel";
import { changesSince, changesSummary, endOfDay, fmtCompareDate, toInputDate } from "./compareModel";
import { summariseValue } from "./compareRows";

/**
 * Look back's Changes tab: what happened since Then, for whatever the card is showing — the whole dial, a
 * section, or one action point. Scoped by the SERVER (the activity feed already narrows to a section or
 * action point and everything under it), started at Then by the server too (`since`, applied before the
 * feed's row cap, so an old Then on a busy dial isn't cut short), and with each field's before and after.
 */
export function ChangesPanel({ instanceId, day, scope, scopeName, onOpenActivity }: {
  instanceId: string;
  day: string;
  scope?: { sectionKey: string; segmentKey?: string | null };
  /** "Finance", or the dial's name at dial level — the header says where the list is from. */
  scopeName: string;
  /** Leave compare mode for the full, dial-wide Activity tab. */
  onOpenActivity?: () => void;
}) {
  const { api, getAccessToken } = useWorkspaceServices();
  const [events, setEvents] = useState<InstanceActivityEvent[] | null>(null);
  const [failed, setFailed] = useState(false);
  const sectionKey = scope?.sectionKey ?? null;
  const segmentKey = scope?.segmentKey ?? null;

  useEffect(() => {
    const token = getAccessToken();
    if (!token) return;
    let cancelled = false;
    setFailed(false);
    setEvents(null);
    api.getInstanceActivity(token, instanceId, sectionKey ? { sectionKey, segmentKey } : undefined, undefined,
      { since: endOfDay(day).toISOString(), includeValues: true })
      .then((all) => { if (!cancelled) setEvents(changesSince(all, day)); })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, [api, getAccessToken, instanceId, sectionKey, segmentKey, day]);

  const date = fmtCompareDate(day);
  return (
    <section className="kd-card" aria-label={`Changes since ${date}`} style={{ padding: "var(--pad-card)", display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 800, color: "var(--text)" }}>
          Since {date} · in {scopeName}
        </h3>
        {events && <span style={{ fontSize: 12.5, color: "var(--text-muted)" }}>{changesSummary(events)}</span>}
      </div>
      {failed ? (
        <p style={{ margin: 0, fontSize: 13, color: "var(--text-faint)" }}>Couldn’t load the changes just now.</p>
      ) : !events ? (
        <p style={{ margin: 0, fontSize: 13, color: "var(--text-faint)" }}>Loading changes…</p>
      ) : events.length > 0 && (
        <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
          {events.slice(0, 50).map((e, i) => (
            <li key={`${e.occurredAtUtc}-${i}`} className="kd-changes-row">
              <span className="kd-changes-date">{fmtCompareDate(toInputDate(new Date(e.occurredAtUtc)))}</span>
              <span style={{ minWidth: 0 }}>
                {e.nodePath && <strong style={{ fontWeight: 700 }}>{e.nodePath} · </strong>}
                {e.description}
                {e.kind === "Attribute" && (e.fromValue != null || e.toValue != null) && (
                  <span className="kd-changes-values">
                    {" "}{summariseValue(undefined, e.fromValue).text} → {summariseValue(undefined, e.toValue).text}
                  </span>
                )}
              </span>
              <span className="kd-changes-who">{activityActor(e)}</span>
            </li>
          ))}
        </ul>
      )}
      {onOpenActivity && (
        <button type="button" className="kd-focusable" onClick={onOpenActivity}
          style={{ alignSelf: "flex-start", font: "inherit", fontSize: 13, fontWeight: 700, background: "none", border: "none", padding: 0, color: "var(--accent-text)", cursor: "pointer" }}>
          Everything since then, dial-wide, in Activity ›
        </button>
      )}
    </section>
  );
}
